import { useEffect } from 'react';
import { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { IntegrationType, WaterfallFormModel, WaterfallViewModel } from '../../models/waterfall';

/**
 * useWaterfallFormSync
 *
 * Keeps the react-hook-form state in sync with the current WaterfallViewModel
 * coming from the backend. This hook exists because the API object (`waterfall`)
 * is the canonical source of truth, but the form holds its own transient local state.
 *
 * Without this, refetches (after create, delete, or activate actions) would leave
 * the form pointing at stale or missing versions.
 *
 * It handles two key sync cases:
 * 1. When a new waterfall loads — reset the form’s name/type fields and ensure the
 *    selected version matches either the explicitly provided initial version or
 *    the current active version from the API.
 * 2. When a version is deleted — detect that the currently selected version no
 *    longer exists and automatically fall back to the previous or latest version.
 *
 * TL;DR: this hook reconciles form state with API data so the UI stays stable
 * after mutations, while still allowing the form to be locally editable.
 */

export function useWaterfallFormSync({
  waterfall,
  initialSelectedVersion,
  reset,
  getValues,
  setValue,
}: {
  waterfall: WaterfallViewModel | null;
  initialSelectedVersion?: number | null;
  reset: UseFormReset<WaterfallFormModel>;
  getValues: UseFormGetValues<WaterfallFormModel>;
  setValue: UseFormSetValue<WaterfallFormModel>;
}) {
  // Reset name/type when a new waterfall loads
  useEffect(() => {
    if (!waterfall) return;
    reset({
      name: waterfall.name ?? '',
      integrationType: waterfall.integrationType ?? IntegrationType.None,
    });

    const current = getValues('selectedVersion');
    const ids = waterfall.versions?.map((v) => v.waterfallVersionId) ?? [];
    if (!current || !ids.includes(current)) {
      setValue('selectedVersion', initialSelectedVersion ?? waterfall.currentVersionId ?? null);
    }
  }, [waterfall?.waterfallId, initialSelectedVersion, reset, setValue]);

  // Handle delete fallback
  useEffect(() => {
    if (!waterfall?.versions?.length) return;
    const ids = waterfall.versions.map((v) => v.waterfallVersionId);
    const current = getValues('selectedVersion');
    if (current && !ids.includes(current)) {
      const sorted = [...waterfall.versions].sort((a, b) => a.waterfallVersionId - b.waterfallVersionId);
      const prev = sorted.filter((v) => v.waterfallVersionId < current).pop();
      setValue('selectedVersion', prev?.waterfallVersionId ?? sorted.at(-1)?.waterfallVersionId ?? null);
    }
  }, [waterfall?.versions?.length]);
}
